<template>
    <!-- Loading -->
    <div class="section text-center mt-3" v-if="loading">
        <div class="spinner-border text-secondary" role="status"></div>
    </div>

    <div v-if="!loading && event" class="event-tracking-page">
        <!-- Event Header Card -->
        <div class="section tracking-block">
            <div class="goals">
                <div class="meeting-card">
                    <div class="meeting-link-static">
                        <div class="in">
                            <div class="meeting-header-compact">
                                <h3 class="meeting-title-compact">{{ event.name }}</h3>
                                <span class="badge" :class="levelBadgeClass(event.event_level)">
                                    {{ formatLevel(event.event_level) }}
                                </span>
                            </div>
                            <div class="meeting-info-compact mt-1">
                                <span class="info-item" v-if="event.date_from">
                                    <ion-icon name="calendar-outline"></ion-icon>
                                    {{ event.date_from }}
                                    <span v-if="event.date_to"> – {{ event.date_to }}</span>
                                </span>
                                <span class="info-item" v-else-if="event.date">
                                    <ion-icon name="calendar-outline"></ion-icon>
                                    {{ event.date }}
                                    <span v-if="event.time"> {{ event.time }}</span>
                                </span>
                                <span class="info-separator" v-if="event.org_level">/</span>
                                <span class="info-item" v-if="event.org_level">
                                    <ion-icon name="people-outline"></ion-icon>
                                    {{ formatOrgLevel(event.org_level) }}
                                </span>
                            </div>
                            <div class="meeting-location-compact" v-if="event.place">
                                <ion-icon name="location-outline"></ion-icon>
                                <span>{{ event.place }}</span>
                            </div>
                            <div class="mt-1 text-muted small" v-if="event.description">
                                {{ event.description }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Section Tabs (Conveners 1-7) -->
        <div class="section tracking-block meetings-tabs-wrap">
            <ul class="nav nav-tabs capsuled meetings-tabs" role="tablist">
                <li class="nav-item" v-for="section in sections" :key="section.id">
                    <a class="nav-link" :class="{ active: activeSection === section.id }"
                        href="javascript:void(0)" role="tab"
                        @click="activeSection = section.id">
                        {{ section.section_number }}. {{ section.name }}
                    </a>
                </li>
            </ul>
        </div>

        <!-- Section Content -->
        <div v-for="section in sections" :key="'content-' + section.id">
            <div v-if="activeSection === section.id">

                <!-- Convener Card -->
                <div class="section tracking-block">
                    <div class="section-heading">
                        <h4>Convener {{ section.section_number }} <span class="text-muted fw-normal">({{ section.name }})</span></h4>
                    </div>
                    <ul class="listview image-listview text inset">
                        <li v-if="section.convener_name">
                            <div class="item">
                                <div class="icon-box bg-primary">
                                    <ion-icon name="person-outline"></ion-icon>
                                </div>
                                <div class="in">
                                    <div>
                                        <strong>{{ section.convener_name }}</strong>
                                        <div class="text-muted small">{{ section.convener_mobile }}</div>
                                    </div>
                                    <button class="btn btn-sm btn-outline-danger"
                                        v-if="permissions.manage_sections"
                                        @click="removeConvener(section)">
                                        <ion-icon name="trash-outline"></ion-icon>
                                    </button>
                                </div>
                            </div>
                        </li>
                        <li v-else>
                            <div class="item">
                                <div class="in text-muted empty-row">No convener assigned</div>
                            </div>
                        </li>
                        <li v-if="permissions.manage_sections">
                            <a href="javascript:void(0)" class="item" @click="openAssignConvener(section)">
                                <div class="icon-box bg-success">
                                    <ion-icon name="person-add-outline"></ion-icon>
                                </div>
                                <div class="in">
                                    <span class="text-success">{{ section.convener_name ? 'Change' : 'Assign' }} Convener</span>
                                </div>
                                <ion-icon name="chevron-forward-outline"></ion-icon>
                            </a>
                        </li>
                    </ul>
                </div>

                <!-- Section Duties -->
                <div class="section tracking-block">
                    <div class="section-heading">
                        <h4>Section duties</h4>
                        <a href="javascript:void(0)" class="link" v-if="permissions.manage_sections"
                            @click="openAddSectionDuty(section)">+ Add</a>
                    </div>
                    <ul class="listview image-listview text inset" v-if="section.duties && section.duties.length">
                        <li v-for="duty in section.duties" :key="duty.id">
                            <div class="item">
                                <div class="icon-box bg-warning">
                                    <ion-icon name="checkmark-outline"></ion-icon>
                                </div>
                                <div class="in">
                                    <div>
                                        {{ duty.duty }}
                                        <div class="text-muted small" v-if="duty.description">{{ duty.description }}</div>
                                    </div>
                                    <button class="btn btn-sm btn-outline-danger"
                                        v-if="permissions.manage_sections"
                                        @click="deleteSectionDuty(duty)">
                                        <ion-icon name="trash-outline"></ion-icon>
                                    </button>
                                </div>
                            </div>
                        </li>
                    </ul>
                    <ul class="listview image-listview text inset" v-else>
                        <li>
                            <div class="item">
                                <div class="in text-muted empty-row">No duties added</div>
                            </div>
                        </li>
                    </ul>
                </div>

                <!-- Task Managers -->
                <div class="section tracking-block">
                    <div class="section-heading">
                        <h4>Task managers</h4>
                        <a href="javascript:void(0)" class="link" v-if="permissions.manage_sections"
                            @click="openAddTaskManager(section)">+ Add</a>
                    </div>
                    <ul class="listview image-listview text inset" v-if="section.task_managers && section.task_managers.length">
                        <li v-for="tm in section.task_managers" :key="tm.id">
                            <div class="item">
                                <div class="icon-box bg-info">
                                    <ion-icon name="briefcase-outline"></ion-icon>
                                </div>
                                <div class="in">
                                    <div>
                                        <strong>{{ tm.member_name }}</strong>
                                        <div class="text-muted small">{{ tm.member_mobile }}</div>
                                    </div>
                                    <button class="btn btn-sm btn-outline-danger"
                                        v-if="permissions.manage_sections"
                                        @click="deleteTaskManager(tm)">
                                        <ion-icon name="trash-outline"></ion-icon>
                                    </button>
                                </div>
                            </div>
                        </li>
                    </ul>
                    <ul class="listview image-listview text inset" v-else>
                        <li>
                            <div class="item">
                                <div class="in text-muted empty-row">No task managers added</div>
                            </div>
                        </li>
                    </ul>
                </div>

                <!-- Wings -->
                <div class="section tracking-block">
                    <div class="section-heading">
                        <h4>Wings</h4>
                    </div>

                    <div v-for="wing in section.wings" :key="wing.id" class="wing-block">
                        <!-- Wing Header -->
                        <div class="wing-header" @click="toggleWing(wing.id)">
                            <div class="wing-header-left">
                                <span class="wing-code">{{ wing.code }}</span>
                                <span class="wing-name">{{ wing.name }}</span>
                            </div>
                            <div class="wing-header-right">
                                <span class="badge bg-light text-dark me-1">
                                    {{ wing.volunteers ? wing.volunteers.length : 0 }} volunteers
                                </span>
                                <ion-icon :name="openWings.includes(wing.id) ? 'chevron-up-outline' : 'chevron-down-outline'"></ion-icon>
                            </div>
                        </div>

                        <!-- Wing Content (collapsible) -->
                        <div v-if="openWings.includes(wing.id)" class="wing-content">
                            <!-- Wing Duties -->
                            <div class="wing-sub-section">
                                <div class="wing-sub-heading">
                                    <span>Duties</span>
                                    <a href="javascript:void(0)" class="link small" v-if="permissions.manage_volunteers"
                                        @click="openAddWingDuty(wing)">+ Add</a>
                                </div>
                                <ul class="listview image-listview text inset" v-if="wing.duties && wing.duties.length">
                                    <li v-for="duty in wing.duties" :key="duty.id">
                                        <div class="item">
                                            <div class="icon-box bg-warning wing-icon">
                                                <ion-icon name="clipboard-outline"></ion-icon>
                                            </div>
                                            <div class="in">
                                                <div class="small">
                                                    {{ duty.duty }}
                                                    <div class="text-muted wing-meta" v-if="duty.description">{{ duty.description }}</div>
                                                </div>
                                                <button class="btn btn-sm btn-outline-danger"
                                                    v-if="permissions.manage_volunteers"
                                                    @click="deleteWingDuty(duty)">
                                                    <ion-icon name="trash-outline"></ion-icon>
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                                <div class="text-muted wing-empty" v-else>No duties</div>
                            </div>

                            <!-- Wing Volunteers -->
                            <div class="wing-sub-section mt-1">
                                <div class="wing-sub-heading">
                                    <span>Volunteers</span>
                                    <a href="javascript:void(0)" class="link small" v-if="permissions.manage_volunteers"
                                        @click="openAddVolunteer(wing)">+ Add</a>
                                </div>
                                <ul class="listview image-listview text inset" v-if="wing.volunteers && wing.volunteers.length">
                                    <li v-for="vol in wing.volunteers" :key="vol.id">
                                        <div class="item">
                                            <div class="icon-box bg-success wing-icon">
                                                <ion-icon name="person-outline"></ion-icon>
                                            </div>
                                            <div class="in">
                                                <div class="small">
                                                    <strong>{{ vol.member_name }}</strong>
                                                    <div class="text-muted wing-meta">{{ vol.member_mobile }}</div>
                                                </div>
                                                <button class="btn btn-sm btn-outline-danger"
                                                    v-if="permissions.manage_volunteers"
                                                    @click="deleteVolunteer(vol)">
                                                    <ion-icon name="trash-outline"></ion-icon>
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                                <div class="text-muted wing-empty" v-else>No volunteers</div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>

    <!-- Assign Convener — bottom sheet -->
    <div class="modal fade action-sheet" id="assignConvenerModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Assign convener</h5>
                    <button type="button" class="close-button" data-bs-dismiss="modal" aria-label="Close">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content tracking-modal-body">
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label">Search member</label>
                                <input type="search" class="form-control" v-model="memberSearch"
                                    @input="searchMembers" placeholder="Name or mobile" autocomplete="off" />
                            </div>
                        </div>
                        <div class="text-center py-2" v-if="memberSearchLoading">
                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                        <ul class="listview image-listview text inset" v-if="memberResults.length">
                            <li v-for="m in memberResults" :key="m.id">
                                <a href="javascript:void(0)" class="item" @click="selectConvener(m)">
                                    <div class="icon-box bg-primary">
                                        <ion-icon name="person-outline"></ion-icon>
                                    </div>
                                    <div class="in">
                                        <div>
                                            <strong>{{ m.name }}</strong>
                                            <div class="text-muted small">{{ m.mobile }}</div>
                                        </div>
                                        <ion-icon name="checkmark-circle-outline"></ion-icon>
                                    </div>
                                </a>
                            </li>
                        </ul>
                        <p class="text-muted small text-center py-2 mb-0" v-if="memberSearch && !memberResults.length && !memberSearchLoading">
                            No members found
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Add Task Manager — bottom sheet -->
    <div class="modal fade action-sheet" id="addTaskManagerModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Add task manager</h5>
                    <button type="button" class="close-button" data-bs-dismiss="modal" aria-label="Close">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content tracking-modal-body">
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label">Search member</label>
                                <input type="search" class="form-control" v-model="memberSearch"
                                    @input="searchMembers" placeholder="Name or mobile" autocomplete="off" />
                            </div>
                        </div>
                        <div class="text-center py-2" v-if="memberSearchLoading">
                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                        <ul class="listview image-listview text inset" v-if="memberResults.length">
                            <li v-for="m in memberResults" :key="m.id">
                                <a href="javascript:void(0)" class="item" @click="selectTaskManager(m)">
                                    <div class="icon-box bg-info">
                                        <ion-icon name="person-outline"></ion-icon>
                                    </div>
                                    <div class="in">
                                        <div>
                                            <strong>{{ m.name }}</strong>
                                            <div class="text-muted small">{{ m.mobile }}</div>
                                        </div>
                                        <ion-icon name="checkmark-circle-outline"></ion-icon>
                                    </div>
                                </a>
                            </li>
                        </ul>
                        <p class="text-muted small text-center py-2 mb-0" v-if="memberSearch && !memberResults.length && !memberSearchLoading">
                            No members found
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Add Section Duty — bottom sheet -->
    <div class="modal fade action-sheet" id="addSectionDutyModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Add section duty</h5>
                    <button type="button" class="close-button" data-bs-dismiss="modal" aria-label="Close">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content tracking-modal-body">
                        <form @submit.prevent="submitSectionDuty">
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label">Duty <span class="text-danger">*</span></label>
                                    <input type="text" class="form-control" v-model="dutyForm.duty" required />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label">Description</label>
                                    <textarea class="form-control" rows="2" v-model="dutyForm.description"></textarea>
                                </div>
                            </div>
                            <div class="form-group basic mb-0">
                                <button type="submit" class="btn btn-primary btn-block btn-lg" :disabled="dutySubmitting">
                                    <span v-if="dutySubmitting" class="spinner-border spinner-border-sm me-1"></span>
                                    Save duty
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Add Wing Duty — bottom sheet -->
    <div class="modal fade action-sheet" id="addWingDutyModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Wing duty — {{ selectedWing?.code }} {{ selectedWing?.name }}</h5>
                    <button type="button" class="close-button" data-bs-dismiss="modal" aria-label="Close">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content tracking-modal-body">
                        <form @submit.prevent="submitWingDuty">
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label">Duty <span class="text-danger">*</span></label>
                                    <input type="text" class="form-control" v-model="wingDutyForm.duty" required />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label">Description</label>
                                    <textarea class="form-control" rows="2" v-model="wingDutyForm.description"></textarea>
                                </div>
                            </div>
                            <div class="form-group basic mb-0">
                                <button type="submit" class="btn btn-primary btn-block btn-lg" :disabled="wingDutySubmitting">
                                    <span v-if="wingDutySubmitting" class="spinner-border spinner-border-sm me-1"></span>
                                    Save duty
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Add Volunteer — bottom sheet -->
    <div class="modal fade action-sheet" id="addVolunteerModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Add volunteer — {{ selectedWing?.code }} {{ selectedWing?.name }}</h5>
                    <button type="button" class="close-button" data-bs-dismiss="modal" aria-label="Close">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content tracking-modal-body">
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label">Search member</label>
                                <input type="search" class="form-control" v-model="memberSearch"
                                    @input="searchMembers" placeholder="Name or mobile" autocomplete="off" />
                            </div>
                        </div>
                        <div class="text-center py-2" v-if="memberSearchLoading">
                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                        <ul class="listview image-listview text inset" v-if="memberResults.length">
                            <li v-for="m in memberResults" :key="m.id">
                                <a href="javascript:void(0)" class="item" @click="selectVolunteer(m)">
                                    <div class="icon-box bg-success">
                                        <ion-icon name="person-outline"></ion-icon>
                                    </div>
                                    <div class="in">
                                        <div>
                                            <strong>{{ m.name }}</strong>
                                            <div class="text-muted small">{{ m.mobile }}</div>
                                        </div>
                                        <ion-icon name="add-circle-outline"></ion-icon>
                                    </div>
                                </a>
                            </li>
                        </ul>
                        <p class="text-muted small text-center py-2 mb-0" v-if="memberSearch && !memberResults.length && !memberSearchLoading">
                            No members found
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Toast Notification -->
    <div class="toast-container position-fixed bottom-0 end-0 p-3" style="z-index:9999;">
        <div class="toast align-items-center text-bg-success border-0" id="successToast" role="alert">
            <div class="d-flex">
                <div class="toast-body">{{ toastMessage }}</div>
                <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
            </div>
        </div>
        <div class="toast align-items-center text-bg-danger border-0" id="errorToast" role="alert">
            <div class="d-flex">
                <div class="toast-body">{{ toastMessage }}</div>
                <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
            </div>
        </div>
    </div>
</template>

<script>
import $ from "jquery";
import UserService from "../../services/user.service";

export default {
    name: "EventTracking",
    data() {
        return {
            loading: true,
            event: null,
            sections: [],
            permissions: { manage_sections: false, manage_volunteers: false },

            activeSection: null,
            openWings: [],

            // Member search (shared across modals)
            memberSearch: "",
            memberSearchLoading: false,
            memberResults: [],
            memberSearchTimer: null,

            // Context for which modal is acting on which entity
            selectedSection: null,
            selectedWing: null,
            activeModal: null,

            // Section duty form
            dutyForm: { duty: "", description: "" },
            dutySubmitting: false,

            // Wing duty form
            wingDutyForm: { duty: "", description: "" },
            wingDutySubmitting: false,

            toastMessage: "",
        };
    },
    mounted() {
        this.eventId = this.$route.params.event_id || this.$route.params.id;
        this.fetchDetail();
    },
    methods: {
        // ── Data loading ──────────────────────────────────────────────────────

        fetchDetail() {
            this.loading = true;
            UserService.authPostRequest("get-event-tracking-detail", { event_id: this.eventId }).then(
                (response) => {
                    if (response.data.status === "success") {
                        this.event = response.data.data.event;
                        this.sections = response.data.data.sections;
                        this.permissions = response.data.data.permissions;
                        if (this.sections.length) {
                            this.activeSection = this.sections[0].id;
                        }
                    }
                    this.loading = false;
                },
                () => { this.loading = false; }
            );
        },

        // ── Wing toggle ───────────────────────────────────────────────────────

        toggleWing(wingId) {
            const idx = this.openWings.indexOf(wingId);
            if (idx >= 0) {
                this.openWings.splice(idx, 1);
            } else {
                this.openWings.push(wingId);
            }
        },

        // ── Member search ─────────────────────────────────────────────────────

        searchMembers() {
            clearTimeout(this.memberSearchTimer);
            if (!this.memberSearch || this.memberSearch.length < 2) {
                this.memberResults = [];
                return;
            }
            this.memberSearchTimer = setTimeout(() => {
                this.memberSearchLoading = true;
                UserService.authPostRequest("search-event-members", { search: this.memberSearch }).then(
                    (response) => {
                        if (response.data.status === "success") {
                            this.memberResults = response.data.data.members;
                        }
                        this.memberSearchLoading = false;
                    },
                    () => { this.memberSearchLoading = false; }
                );
            }, 400);
        },

        resetMemberSearch() {
            this.memberSearch = "";
            this.memberResults = [];
        },

        // ── Assign Convener ───────────────────────────────────────────────────

        openAssignConvener(section) {
            this.selectedSection = section;
            this.activeModal = "convener";
            this.resetMemberSearch();
            $("#assignConvenerModal").modal("show");
        },

        selectConvener(member) {
            UserService.authPostRequest("store-event-section-convener", {
                section_id: this.selectedSection.id,
                member_id: member.id,
            }).then((response) => {
                if (response.data.status === "success") {
                    const sec = this.sections.find(s => s.id === this.selectedSection.id);
                    if (sec) {
                        sec.convener_member_id = member.id;
                        sec.convener_name = member.name;
                        sec.convener_mobile = member.mobile;
                    }
                    $("#assignConvenerModal").modal("hide");
                    this.showToast("Convener assigned successfully", "success");
                }
            });
        },

        removeConvener(section) {
            if (!confirm("Remove convener?")) return;
            UserService.authPostRequest("remove-event-section-convener", { section_id: section.id }).then(
                (response) => {
                    if (response.data.status === "success") {
                        const sec = this.sections.find(s => s.id === section.id);
                        if (sec) {
                            sec.convener_member_id = null;
                            sec.convener_name = null;
                            sec.convener_mobile = null;
                        }
                        this.showToast("Convener removed", "success");
                    }
                }
            );
        },

        // ── Section Duties ────────────────────────────────────────────────────

        openAddSectionDuty(section) {
            this.selectedSection = section;
            this.dutyForm = { duty: "", description: "" };
            this.dutySubmitting = false;
            $("#addSectionDutyModal").modal("show");
        },

        submitSectionDuty() {
            this.dutySubmitting = true;
            UserService.authPostRequest("store-section-duty", {
                section_id: this.selectedSection.id,
                duty: this.dutyForm.duty,
                description: this.dutyForm.description,
            }).then((response) => {
                this.dutySubmitting = false;
                if (response.data.status === "success") {
                    const sec = this.sections.find(s => s.id === this.selectedSection.id);
                    if (sec) sec.duties.push(response.data.data.duty);
                    $("#addSectionDutyModal").modal("hide");
                    this.showToast("Duty added", "success");
                }
            }, () => { this.dutySubmitting = false; });
        },

        deleteSectionDuty(duty) {
            if (!confirm("Delete this duty?")) return;
            UserService.authPostRequest("delete-section-duty", { duty_id: duty.id }).then(
                (response) => {
                    if (response.data.status === "success") {
                        this.sections.forEach(sec => {
                            sec.duties = sec.duties.filter(d => d.id !== duty.id);
                        });
                        this.showToast("Duty deleted", "success");
                    }
                }
            );
        },

        // ── Task Managers ─────────────────────────────────────────────────────

        openAddTaskManager(section) {
            this.selectedSection = section;
            this.activeModal = "taskmanager";
            this.resetMemberSearch();
            $("#addTaskManagerModal").modal("show");
        },

        selectTaskManager(member) {
            UserService.authPostRequest("store-section-task-manager", {
                section_id: this.selectedSection.id,
                member_id: member.id,
            }).then((response) => {
                if (response.data.status === "success") {
                    const sec = this.sections.find(s => s.id === this.selectedSection.id);
                    if (sec) sec.task_managers.push(response.data.data.task_manager);
                    $("#addTaskManagerModal").modal("hide");
                    this.showToast("Task manager added", "success");
                } else {
                    this.showToast(response.data.message, "error");
                }
            });
        },

        deleteTaskManager(tm) {
            if (!confirm("Remove task manager?")) return;
            UserService.authPostRequest("delete-section-task-manager", { task_manager_id: tm.id }).then(
                (response) => {
                    if (response.data.status === "success") {
                        this.sections.forEach(sec => {
                            sec.task_managers = sec.task_managers.filter(t => t.id !== tm.id);
                        });
                        this.showToast("Task manager removed", "success");
                    }
                }
            );
        },

        // ── Wing Duties ───────────────────────────────────────────────────────

        openAddWingDuty(wing) {
            this.selectedWing = wing;
            this.wingDutyForm = { duty: "", description: "" };
            this.wingDutySubmitting = false;
            $("#addWingDutyModal").modal("show");
        },

        submitWingDuty() {
            this.wingDutySubmitting = true;
            UserService.authPostRequest("store-wing-duty", {
                wing_id: this.selectedWing.id,
                duty: this.wingDutyForm.duty,
                description: this.wingDutyForm.description,
            }).then((response) => {
                this.wingDutySubmitting = false;
                if (response.data.status === "success") {
                    this.sections.forEach(sec => {
                        const w = sec.wings?.find(w => w.id === this.selectedWing.id);
                        if (w) w.duties.push(response.data.data.duty);
                    });
                    $("#addWingDutyModal").modal("hide");
                    this.showToast("Wing duty added", "success");
                }
            }, () => { this.wingDutySubmitting = false; });
        },

        deleteWingDuty(duty) {
            if (!confirm("Delete this wing duty?")) return;
            UserService.authPostRequest("delete-wing-duty", { duty_id: duty.id }).then(
                (response) => {
                    if (response.data.status === "success") {
                        this.sections.forEach(sec => {
                            sec.wings?.forEach(w => {
                                w.duties = w.duties.filter(d => d.id !== duty.id);
                            });
                        });
                        this.showToast("Wing duty deleted", "success");
                    }
                }
            );
        },

        // ── Volunteers ────────────────────────────────────────────────────────

        openAddVolunteer(wing) {
            this.selectedWing = wing;
            this.activeModal = "volunteer";
            this.resetMemberSearch();
            $("#addVolunteerModal").modal("show");
        },

        selectVolunteer(member) {
            UserService.authPostRequest("store-wing-volunteer", {
                wing_id: this.selectedWing.id,
                member_id: member.id,
            }).then((response) => {
                if (response.data.status === "success") {
                    this.sections.forEach(sec => {
                        const w = sec.wings?.find(w => w.id === this.selectedWing.id);
                        if (w) w.volunteers.push(response.data.data.volunteer);
                    });
                    $("#addVolunteerModal").modal("hide");
                    this.showToast("Volunteer added", "success");
                } else {
                    this.showToast(response.data.message, "error");
                }
            });
        },

        deleteVolunteer(vol) {
            if (!confirm("Remove volunteer?")) return;
            UserService.authPostRequest("delete-wing-volunteer", { volunteer_id: vol.id }).then(
                (response) => {
                    if (response.data.status === "success") {
                        this.sections.forEach(sec => {
                            sec.wings?.forEach(w => {
                                w.volunteers = w.volunteers.filter(v => v.id !== vol.id);
                            });
                        });
                        this.showToast("Volunteer removed", "success");
                    }
                }
            );
        },

        // ── Helpers ───────────────────────────────────────────────────────────

        showToast(message, type = "success") {
            this.toastMessage = message;
            const id = type === "success" ? "successToast" : "errorToast";
            const toastEl = document.getElementById(id);
            if (toastEl) {
                const toast = new window.bootstrap.Toast(toastEl, { delay: 2500 });
                toast.show();
            }
        },

        formatLevel(level) {
            const map = { state: "State", district: "District", mandalam: "Mandalam", unit: "Unit" };
            return map[level] || level;
        },

        formatOrgLevel(level) {
            const map = {
                organisation: "Organisation", youth: "Youth", students: "Students",
                women: "Women", girls: "Girls", balavedi: "Balavedi", all: "ALL",
            };
            return map[level] || level;
        },

        levelBadgeClass(level) {
            const map = { state: "bg-danger", district: "bg-primary", mandalam: "bg-success", unit: "bg-warning text-dark" };
            return map[level] || "bg-secondary";
        },
    },
};
</script>

<style scoped>
.event-tracking-page {
    padding-bottom: 12px;
}

.tracking-block {
    margin-top: 10px !important;
}

.meeting-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    margin: 0 16px;
    overflow: hidden;
    border: 1px solid #eef0f3;
}
.meeting-link-static {
    padding: 12px 14px;
}
.meeting-header-compact {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}
.meeting-title-compact { font-size: 15px; font-weight: 700; color: #1a1a2e; margin: 0; flex: 1; line-height: 1.3; }
.meeting-info-compact { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.info-item { display: flex; align-items: center; gap: 3px; font-size: 12px; color: #6c757d; }
.info-separator { color: #ccc; }
.meeting-location-compact { display: flex; align-items: center; gap: 4px; font-size: 12px; color: #6c757d; margin-top: 4px; }

.meetings-tabs-wrap { padding: 0 16px; }
.meetings-tabs {
    white-space: nowrap;
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    background: #e8ebf0;
    border: 1px solid #d5dae3;
    padding: 4px;
    border-radius: 10px;
    gap: 2px;
    display: flex;
}

.meetings-tabs::-webkit-scrollbar {
    display: none;
}

.meetings-tabs-wrap :deep(.nav-item) {
    flex: 0 0 auto;
}

.meetings-tabs-wrap :deep(.nav-link) {
    color: #4b5563;
    font-weight: 600;
    font-size: 11px;
    line-height: 1.2;
    padding: 8px 10px;
    border-radius: 8px;
    border: none;
    background: transparent;
    white-space: nowrap;
}

.meetings-tabs-wrap :deep(.nav-link.active) {
    color: #1a418c;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    margin-bottom: 4px;
}
.section-heading h4 { font-size: 13px; font-weight: 600; margin: 0; color: #374151; }
.section-heading .link { font-size: 13px; color: #1a418c; font-weight: 500; }

.empty-row {
    padding: 10px 0;
    font-size: 13px;
}

/* Bottom sheet body scroll area */
.tracking-modal-body {
    max-height: min(72vh, 520px);
    padding-top: 4px !important;
}

/* Wing block */
.wing-block {
    margin: 0 16px 8px;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
}
.wing-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: #f8f9fb;
    cursor: pointer;
    user-select: none;
    min-height: 48px;
}
.wing-header-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
.wing-code {
    background: #1a418c;
    color: #fff;
    border-radius: 6px;
    padding: 2px 7px;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
}
.wing-name { font-size: 13px; font-weight: 600; color: #333; overflow: hidden; text-overflow: ellipsis; }
.wing-header-right { display: flex; align-items: center; gap: 6px; flex-shrink: 0; font-size: 11px; }

.wing-content { padding: 6px 0 8px; }
.wing-sub-section { padding: 0; }
.wing-sub-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px 4px;
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
.wing-sub-heading .link { font-size: 12px; color: #1a418c; text-transform: none; font-weight: 500; }

.wing-icon {
    min-width: 36px;
    height: 36px;
}

.wing-meta {
    font-size: 11px;
}

.wing-empty {
    font-size: 12px;
    text-align: center;
    padding: 8px 0;
}

.icon-box {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    min-width: 36px;
    height: 36px;
}
.icon-box ion-icon { color: #fff; font-size: 16px; }
</style>

